import React, { useState, useEffect } from 'react';
import { sponsorshipService } from '../../../Services/sponsorshipService';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';
import { AlertCircle, X } from 'lucide-react';

const ContractForm = ({ contract, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [loadingSponsors, setLoadingSponsors] = useState(true);
  const [formData, setFormData] = useState({
    sponsor_id: '',
    coverage_scope: 'national',
    territories: [],
    start_date: '',
    end_date: '',
    weeks_purchased: '',
    investment_amount: '',
    payment_terms: '',
    special_requirements: '',
  });

  useEffect(() => {
    loadSponsors();
  }, []);

  useEffect(() => {
    if (contract) {
      setFormData({
        sponsor_id: contract.sponsor_id || '',
        coverage_scope: contract.coverage_scope || 'national',
        territories: contract.territories || [],
        start_date: contract.start_date || '',
        end_date: contract.end_date || '',
        weeks_purchased: contract.weeks_purchased || '',
        investment_amount: contract.investment_amount || '',
        payment_terms: contract.payment_terms || '',
        special_requirements: contract.special_requirements || '',
      });
    }
  }, [contract]);

  const loadSponsors = async () => {
    try {
      setLoadingSponsors(true);
      const response = await sponsorshipService.sponsors.list({ is_active: true });
      setSponsors(response.sponsors || []);
    } catch (err) {
      console.error('Failed to load sponsors:', err);
      setError('Failed to load sponsors list');
    } finally {
      setLoadingSponsors(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTerritoriesChange = (value) => {
    const territories = value
      .split(',')
      .map(t => t.trim())
      .filter(t => t)
      .map(state => ({ state }));
    setFormData(prev => ({
      ...prev,
      territories
    }));
  };

  const validateForm = () => {
    if (!formData.sponsor_id) {
      setError('Please select a sponsor');
      return false;
    }
    if (!formData.start_date) {
      setError('Please select a start date');
      return false;
    }
    if (!formData.end_date) {
      setError('Please select an end date');
      return false;
    }
    if (new Date(formData.end_date) <= new Date(formData.start_date)) {
      setError('End date must be after start date');
      return false;
    }
    if (!formData.weeks_purchased || formData.weeks_purchased <= 0) {
      setError('Please enter a valid number of weeks purchased');
      return false;
    }
    if (formData.coverage_scope === 'territory' && formData.territories.length === 0) {
      setError('Please specify at least one territory for territory-based coverage');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const contractData = {
        sponsor_id: formData.sponsor_id,
        coverage_scope: formData.coverage_scope,
        start_date: formData.start_date,
        end_date: formData.end_date,
        weeks_purchased: parseInt(formData.weeks_purchased),
      };

      if (formData.coverage_scope === 'territory') {
        contractData.territories = formData.territories;
      }

      if (formData.investment_amount) {
        contractData.investment_amount = parseFloat(formData.investment_amount);
      }

      if (formData.payment_terms) {
        contractData.payment_terms = formData.payment_terms;
      }

      if (formData.special_requirements) {
        contractData.special_requirements = formData.special_requirements;
      }

      if (contract) {
        await sponsorshipService.contracts.update(contract._id, contractData);
      } else {
        await sponsorshipService.contracts.create(contractData);
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save contract');
    } finally {
      setLoading(false);
    }
  };

  const getTerritoriesString = () => {
    return formData.territories.map(t => t.state).join(', ');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl my-8 rounded-lg border bg-background shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            {contract ? 'Edit Contract' : 'Create New Contract'}
          </h2>
          <Button
            type="button"
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="max-h-[calc(100vh-12rem)] overflow-y-auto">
          <div className="space-y-6 px-6 py-4">
            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            {/* Sponsor Selection */}
            <div className="space-y-2">
              <Label htmlFor="sponsor">
                Sponsor <span className="text-destructive">*</span>
              </Label>
              {loadingSponsors ? (
                <div className="text-sm text-muted-foreground">Loading sponsors...</div>
              ) : (
                <select
                  id="sponsor"
                  className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={formData.sponsor_id}
                  onChange={(e) => handleInputChange('sponsor_id', e.target.value)}
                  required
                  disabled={!!contract}
                >
                  <option value="">Select a sponsor</option>
                  {sponsors.map((sponsor) => (
                    <option key={sponsor._id} value={sponsor._id}>
                      {sponsor.name}
                    </option>
                  ))}
                </select>
              )}
              {contract && (
                <p className="text-xs text-muted-foreground">
                  Sponsor cannot be changed after contract creation
                </p>
              )}
            </div>

            {/* Coverage Scope */}
            <div className="space-y-2">
              <Label htmlFor="coverage-scope">
                Coverage Scope <span className="text-destructive">*</span>
              </Label>
              <select
                id="coverage-scope"
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={formData.coverage_scope}
                onChange={(e) => handleInputChange('coverage_scope', e.target.value)}
                required
              >
                <option value="national">National</option>
                <option value="territory">Territory</option>
              </select>
            </div>

            {/* Territories (if territory scope) */}
            {formData.coverage_scope === 'territory' && (
              <div className="space-y-2">
                <Label htmlFor="territories">
                  Territories (States) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="territories"
                  placeholder="e.g., CA, NY, TX"
                  value={getTerritoriesString()}
                  onChange={(e) => handleTerritoriesChange(e.target.value)}
                  required={formData.coverage_scope === 'territory'}
                />
                <p className="text-xs text-muted-foreground">
                  Enter state codes separated by commas
                </p>
              </div>
            )}

            {/* Date Range */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="start-date">
                  Start Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="start-date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleInputChange('start_date', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date">
                  End Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="end-date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleInputChange('end_date', e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Weeks Purchased */}
            <div className="space-y-2">
              <Label htmlFor="weeks-purchased">
                Weeks Purchased <span className="text-destructive">*</span>
              </Label>
              <Input
                id="weeks-purchased"
                type="number"
                min="1"
                placeholder="e.g., 1000"
                value={formData.weeks_purchased}
                onChange={(e) => handleInputChange('weeks_purchased', e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Total number of sponsorship weeks purchased
              </p>
            </div>

            {/* Investment Amount */}
            <div className="space-y-2">
              <Label htmlFor="investment-amount">Investment Amount</Label>
              <Input
                id="investment-amount"
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g., 50000.00"
                value={formData.investment_amount}
                onChange={(e) => handleInputChange('investment_amount', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Total investment amount in dollars
              </p>
            </div>

            {/* Payment Terms */}
            <div className="space-y-2">
              <Label htmlFor="payment-terms">Payment Terms</Label>
              <Textarea
                id="payment-terms"
                rows={2}
                placeholder="e.g., Net 30, 50% upfront"
                value={formData.payment_terms}
                onChange={(e) => handleInputChange('payment_terms', e.target.value)}
              />
            </div>

            {/* Special Requirements */}
            <div className="space-y-2">
              <Label htmlFor="special-requirements">Special Requirements</Label>
              <Textarea
                id="special-requirements"
                rows={3}
                placeholder="Any special requirements or notes for this contract"
                value={formData.special_requirements}
                onChange={(e) => handleInputChange('special_requirements', e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t bg-muted/40 px-6 py-4 sticky bottom-0">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || loadingSponsors}>
              {loading ? 'Saving...' : (contract ? 'Update Contract' : 'Create Contract')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContractForm;
